import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
	ProductCost,
	ProductCostCurrency,
	ProductCostKind,
	ProductCostPayload,
	ProductCostPaymentMethod,
	ProductCostPrice,
} from './product-costs.model';
import { Result } from '@/reservation/reservation.model';
import { Product } from '../product.model';

@Injectable({
	providedIn: 'root',
})
export class ProductCostsApiService {
	private readonly apiUrl = environment.apiUrl;
	private readonly httpClient = inject(HttpClient);

	getProductCosts(productId: number): Observable<ProductCost[]> {
		return this.httpClient
			.get<ProductCost[]>(`${this.apiUrl}/product/cost/`, {
				params: { product: productId },
			})
			.pipe(map((costs) => costs.map((cost) => this.mapCostPricesFromApi(cost))));
	}

	createProductCost(payload: ProductCostPayload): Observable<ProductCost> {
		return this.httpClient
			.post<ProductCost>(`${this.apiUrl}/product/cost/`, this.mapCostPricesToApi(payload))
			.pipe(map((cost) => this.mapCostPricesFromApi(cost)));
	}

	updateProductCost(costId: number, payload: ProductCostPayload): Observable<ProductCost> {
		return this.httpClient
			.put<ProductCost>(`${this.apiUrl}/product/cost/${costId}/`, this.mapCostPricesToApi(payload))
			.pipe(map((cost) => this.mapCostPricesFromApi(cost)));
	}

	getProductCost(costId: number): Observable<ProductCost> {
		return this.httpClient
			.get<ProductCost>(`${this.apiUrl}/product/cost/${costId}/`)
			.pipe(map((cost) => this.mapCostPricesFromApi(cost)));
	}

	patchProductCost(costId: number, payload: Partial<ProductCost>): Observable<ProductCost> {
		return this.httpClient
			.patch<ProductCost>(`${this.apiUrl}/product/cost/${costId}/`, this.mapCostPricesToApi(payload))
			.pipe(map((cost) => this.mapCostPricesFromApi(cost)));
	}

	getCurrencies(): Observable<ProductCostCurrency[]> {
		return this.httpClient
			.get<Result<ProductCostCurrency>>(`${this.apiUrl}/currency/`)
			.pipe(map((result) => result.results ?? []));
	}

	getCostKinds(): Observable<ProductCostKind[]> {
		return this.httpClient
			.get<Result<ProductCostKind>>(`${this.apiUrl}/product/cost_kind/`)
			.pipe(map((result) => result.results ?? []));
	}

	getPaymentMethods(): Observable<ProductCostPaymentMethod[]> {
		return this.httpClient
			.get<Result<ProductCostPaymentMethod>>(`${this.apiUrl}/product/payment_method/`)
			.pipe(map((result) => result.results ?? []));
	}

	patchProductCosts(productId: number, payload: { costs: number[] }): Observable<Product> {
		return this.httpClient.patch<Product>(`${this.apiUrl}/product/${productId}/`, payload);
	}

	private mapCostPricesFromApi(cost: ProductCost): ProductCost {
		return {
			...cost,
			prices: cost.prices.map((price) => ({
				...price,
				price: price.price / 100,
			})),
		};
	}

	private mapCostPricesToApi<T extends { prices?: ProductCostPrice[] }>(payload: T): T {
		if (!payload.prices) {
			return payload;
		}
		return {
			...payload,
			prices: payload.prices.map((price) => ({
				...price,
				price: Math.round(price.price * 100),
			})),
		};
	}
}
